import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faEye,
  faEyeSlash,
  faArrowUp,
  faArrowDown,
  faStethoscope,
  faImage,
  faSpinner,
  faExclamationTriangle,
  faSearch,
  faFilter,
  faChartLine,
  faList,
  faCheckCircle,
  faBan,
  faInfoCircle,
  faTags,
  faEuroSign,
  faHeart,
  faCog,
  faStar,
  faLayerGroup,
  faToggleOn,
  faToggleOff
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "../../lib/supabase";

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    image_url: "",
    is_active: true,
  });

  const categories = [
    "Consultații",
    "Investigații",
    "Proceduri",
    "Documente",
    "Terapii",
    "Analize",
  ];

  useEffect(() => {
    loadServices();
    loadStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadServices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("services")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (err) {
      setError("Eroare la încărcarea serviciilor");
      console.error("Error loading services:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await supabase
        .from("services")
        .select("*");

      if (error) throw error;

      const allServices = data || [];
      const activeServices = allServices.filter(s => s.is_active);
      const inactiveServices = allServices.filter(s => !s.is_active);
      
      const categoryStats = categories.reduce((acc, category) => {
        acc[category] = allServices.filter(s => s.category === category).length;
        return acc;
      }, {});

      setStats({
        total: allServices.length,
        active: activeServices.length,
        inactive: inactiveServices.length,
        categories: categoryStats,
        mostPopularCategory: Object.entries(categoryStats).reduce((a, b) => 
          categoryStats[a] > categoryStats[b[0]] ? a : b[0], categories[0]
        )
      });
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      price: "",
      category: "",
      image_url: "",
      is_active: true,
    });
    setEditingService(null);
    setShowAddForm(false);
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      const newOrderIndex =
        Math.max(...services.map((s) => s.order_index || 0), 0) + 1;

      const { data, error } = await supabase
        .from("services")
        .insert([
          {
            ...formData,
            order_index: newOrderIndex,
          },
        ])
        .select();

      if (error) throw error;

      setServices([...services, ...data]);
      resetForm();
      showMessage("Serviciu adăugat cu succes!");
      await loadStats();
    } catch (error) {
      console.error("Error adding service:", error);
      showMessage("Eroare la adăugarea serviciului", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleEditService = (service) => {
    setFormData({
      title: service.title || "",
      description: service.description || "",
      price: service.price || "",
      category: service.category || "",
      image_url: service.image_url || "",
      is_active: service.is_active !== false,
    });
    setEditingService(service.id);
    setShowAddForm(false);
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      const { error } = await supabase
        .from("services")
        .update(formData)
        .eq("id", editingService)
        .select();

      if (error) throw error;

      setServices(
        services.map((s) =>
          s.id === editingService ? { ...s, ...formData } : s
        )
      );
      resetForm();
      showMessage("Serviciu actualizat cu succes!");
      await loadStats();
    } catch (error) {
      console.error("Error updating service:", error);
      showMessage("Eroare la actualizarea serviciului", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Sigur doriți să ștergeți acest serviciu?")) return;

    try {
      setUpdating(true);
      const { error } = await supabase
        .from("services")
        .delete()
        .eq("id", serviceId);

      if (error) throw error;

      setServices(services.filter((s) => s.id !== serviceId));
      showMessage("Serviciu șters cu succes!");
      await loadStats();
    } catch (error) {
      console.error("Error deleting service:", error);
      showMessage("Eroare la ștergerea serviciului", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleActive = async (serviceId, currentStatus) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from("services")
        .update({ is_active: !currentStatus })
        .eq("id", serviceId);

      if (error) throw error;

      setServices(
        services.map((s) =>
          s.id === serviceId ? { ...s, is_active: !currentStatus } : s
        )
      );
      showMessage(`Serviciu ${!currentStatus ? "activat" : "dezactivat"}!`);
      await loadStats();
    } catch (error) {
      console.error("Error toggling service status:", error);
      showMessage("Eroare la schimbarea statusului", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleReorderService = async (serviceId, direction) => {
    try {
      setUpdating(true);
      const currentIndex = services.findIndex((s) => s.id === serviceId);
      if (
        (direction === "up" && currentIndex === 0) ||
        (direction === "down" && currentIndex === services.length - 1)
      ) {
        return;
      }

      const targetIndex =
        direction === "up" ? currentIndex - 1 : currentIndex + 1;
      const currentService = services[currentIndex];
      const targetService = services[targetIndex];

      await supabase
        .from("services")
        .update({ order_index: targetService.order_index })
        .eq("id", currentService.id);

      await supabase
        .from("services")
        .update({ order_index: currentService.order_index })
        .eq("id", targetService.id);

      await loadServices();
      showMessage("Ordinea serviciilor a fost actualizată!");
    } catch (error) {
      console.error("Error reordering services:", error);
      showMessage("Eroare la reordonarea serviciilor", "error");
    } finally {
      setUpdating(false);
    }
  };

  const filteredServices = services.filter((service) => {
    const searchMatch =
      service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.price?.toLowerCase().includes(searchTerm.toLowerCase());

    const categoryMatch = filterCategory === "all" || service.category === filterCategory;
    const statusMatch = filterStatus === "all" || 
                       (filterStatus === "active" && service.is_active) ||
                       (filterStatus === "inactive" && !service.is_active);

    return searchMatch && categoryMatch && statusMatch;
  });

  if (loading) {
    return (
      <div className="admin-content">
        <div className="booking-loading-container">
          <div className="booking-loading-content">
            <div className="loading-spinner">
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
            <h3>Se încarcă serviciile...</h3>
            <p>Vă rugăm să așteptați</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content">
        <div className="booking-error-container">
          <div className="booking-error-content">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              size="3x"
              className="error-icon"
            />
            <h3>Oops! Ceva nu a mers bine</h3>
            <p className="error-message">{error}</p>
            <button className="btn-retry" onClick={loadServices}>
              <FontAwesomeIcon icon={faSpinner} className="me-2" />
              Încearcă din nou
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content booking-manager-wrapper">
      {/* Enhanced Header */}
      <div className="booking-header-section">
        <div className="header-background-pattern"></div>
        <div className="booking-header-content">
          <div className="header-icon-wrapper">
            <FontAwesomeIcon icon={faStethoscope} className="header-main-icon" />
          </div>
          <div className="header-text">
            <h1 className="page-title">
              Managementul Serviciilor Medicale
              <span className="title-accent"></span>
            </h1>
            <p className="page-subtitle">
              Gestionează eficient serviciile medicale oferite de clinică și monitorizează categoriile acestora
            </p>
          </div>
          <button className="btn-refresh" onClick={loadServices}>
            <FontAwesomeIcon icon={faSpinner} className="refresh-icon" />
            <span>Actualizează</span>
          </button>
        </div>
      </div>

      {/* Enhanced Statistics Cards */}
      {stats && (
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card total-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faList} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Servicii</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
            </div>

            <div className="stat-card confirmed-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faCheckCircle} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.active}</div>
                <div className="stat-label">Servicii Active</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faToggleOn} />
              </div>
            </div>

            <div className="stat-card cancelled-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faBan} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.inactive}</div>
                <div className="stat-label">Servicii Inactive</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faToggleOff} />
              </div>
            </div>

            <div className="stat-card completed-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faTags} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{Object.keys(stats.categories).length}</div>
                <div className="stat-label">Categorii</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faLayerGroup} />
              </div>
            </div>

            <div className="stat-card weekly-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faStar} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.categories[stats.mostPopularCategory] || 0}</div>
                <div className="stat-label">Top Categorie</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faHeart} />
              </div>
            </div>

            <div className="stat-card pending-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faPlus} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">
                  <FontAwesomeIcon icon={faPlus} />
                </div>
                <div className="stat-label">Adaugă Serviciu</div>
              </div>
              <div className="stat-trend" onClick={() => setShowAddForm(true)} style={{cursor: 'pointer'}}>
                <FontAwesomeIcon icon={faCog} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Filters and Search */}
      <div className="filters-section">
        <div className="filters-card">
          <div className="search-wrapper">
            <div className="search-input-container">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Caută după nume, descriere, categorie sau preț..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>

          <div className="filter-wrapper">
            <div className="filter-dropdown-container">
              <FontAwesomeIcon icon={faTags} className="filter-icon" />
              <select
                className="filter-select"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">Toate categoriile</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-wrapper">
            <div className="filter-dropdown-container">
              <FontAwesomeIcon icon={faFilter} className="filter-icon" />
              <select
                className="filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Toate statusurile</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="export-wrapper">
            <button 
              className="btn-export"
              onClick={() => setShowAddForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="export-icon" />
              <span>Adaugă Serviciu</span>
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          <div className="results-info">
            <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
            <span>
              Se afișează <strong>{filteredServices.length}</strong> din <strong>{services.length}</strong> servicii
              {searchTerm && ` pentru "${searchTerm}"`}
            </span>
          </div>
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`alert alert-${messageType} mb-4`}>
          <FontAwesomeIcon 
            icon={messageType === 'success' ? faCheckCircle : faExclamationTriangle} 
            className="me-2" 
          />
          {message}
        </div>
      )}

      {/* Service Form Modal */}
      {(showAddForm || editingService) && (
        <div className="booking-modal-overlay">
          <div className="booking-modal">
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <FontAwesomeIcon icon={editingService ? faEdit : faPlus} />
                </div>
                <div className="modal-title-section">
                  <h2 className="modal-title">
                    {editingService ? "Editează Serviciu" : "Adaugă Serviciu Nou"}
                  </h2>
                  <p className="modal-subtitle">
                    {editingService ? "Modifică detaliile serviciului medical" : "Completează informațiile pentru noul serviciu"}
                  </p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={resetForm}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={editingService ? handleUpdateService : handleAddService}>
                <div className="modal-content-grid">
                  <div className="info-section">
                    <h4 className="section-title">
                      <FontAwesomeIcon icon={faInfoCircle} className="section-icon" />
                      Informații de bază
                    </h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <label className="info-label">Numele Serviciului *</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          placeholder="ex. Consultație Pneumologică"
                        />
                      </div>

                      <div className="info-item">
                        <label className="info-label">Categoria *</label>
                        <select
                          name="category"
                          className="form-control"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Selectează categoria</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="info-item">
                        <label className="info-label">Preț *</label>
                        <input
                          type="text"
                          name="price"
                          className="form-control"
                          value={formData.price}
                          onChange={handleInputChange}
                          required
                          placeholder="ex. 150 RON"
                        />
                      </div>

                      <div className="info-item">
                        <label className="info-label">URL Imagine</label>
                        <input
                          type="url"
                          name="image_url"
                          className="form-control"
                          value={formData.image_url}
                          onChange={handleInputChange}
                          placeholder="https://exemple.com/imagine.jpg"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="info-section">
                    <h4 className="section-title">
                      <FontAwesomeIcon icon={faEdit} className="section-icon" />
                      Descriere detaliată
                    </h4>
                    <div className="info-grid">
                      <div className="info-item full-width">
                        <label className="info-label">Descrierea Serviciului *</label>
                        <textarea
                          name="description"
                          className="form-control"
                          rows="4"
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          placeholder="Descrierea detaliată a serviciului medical..."
                        />
                      </div>

                      <div className="info-item">
                        <label className="info-label checkbox-label">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleInputChange}
                          />
                          <span className="checkmark"></span>
                          Serviciu activ
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-modal-secondary"
                onClick={resetForm}
                disabled={updating}
              >
                <FontAwesomeIcon icon={faTimes} className="btn-icon" />
                Anulează
              </button>
              <button
                type="submit"
                className="btn-modal-primary"
                onClick={editingService ? handleUpdateService : handleAddService}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="btn-icon" />
                    {editingService ? "Se actualizează..." : "Se adaugă..."}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="btn-icon" />
                    {editingService ? "Actualizează" : "Adaugă"} Serviciu
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Services Table */}
      <div className="bookings-table-section">
        <div className="table-card">
          <div className="table-header">
            <h3>
              <FontAwesomeIcon icon={faList} className="table-icon" />
              Lista Serviciilor Medicale
            </h3>
          </div>
          
          <div className="table-container">
            {filteredServices.length === 0 ? (
              <div className="empty-state">
                <div className="empty-content">
                  <FontAwesomeIcon icon={faStethoscope} className="empty-icon" />
                  <h4>Nu există servicii</h4>
                  <p>
                    {searchTerm || filterCategory !== "all" || filterStatus !== "all"
                      ? "Nu s-au găsit servicii care să corespundă criteriilor de căutare."
                      : "Adaugă primul serviciu medical pentru a începe."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="services-grid-container">
                {filteredServices.map((service, index) => (
                  <div key={service.id} className="service-card-modern">
                    <div className="service-card-header">
                      <div className="service-image-container">
                        {service.image_url ? (
                          <img 
                            src={service.image_url} 
                            alt={service.title}
                            className="service-card-image"
                          />
                        ) : (
                          <div className="service-card-placeholder">
                            <FontAwesomeIcon icon={faStethoscope} />
                          </div>
                        )}
                      </div>
                      <div className="service-card-status">
                        <span className={`status-badge ${service.is_active ? 'status-confirmed' : 'status-cancelled'}`}>
                          <FontAwesomeIcon 
                            icon={service.is_active ? faCheckCircle : faBan} 
                            className="status-icon" 
                          />
                          {service.is_active ? 'Activ' : 'Inactiv'}
                        </span>
                      </div>
                    </div>

                    <div className="service-card-content">
                      <div className="service-main-info">
                        <h4 className="service-title">{service.title}</h4>
                        <p className="service-description">
                          {service.description?.substring(0, 120)}
                          {service.description?.length > 120 && "..."}
                        </p>
                      </div>

                      <div className="service-meta-info">
                        <div className="service-category">
                          <FontAwesomeIcon icon={faTags} className="meta-icon" />
                          <span>{service.category}</span>
                        </div>
                        <div className="service-price">
                          <FontAwesomeIcon icon={faEuroSign} className="meta-icon" />
                          <span>{service.price}</span>
                        </div>
                      </div>
                    </div>

                    <div className="service-card-actions">
                      <div className="action-buttons-grid">
                        <button
                          className="action-btn view-btn"
                          onClick={() => setSelectedService(service)}
                          title="Vezi detalii"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          className="action-btn confirm-btn"
                          onClick={() => handleEditService(service)}
                          title="Editează"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleToggleActive(service.id, service.is_active)}
                          title={service.is_active ? "Dezactivează" : "Activează"}
                          style={{
                            backgroundColor: service.is_active ? '#dc3545' : '#28a745',
                            borderColor: service.is_active ? '#dc3545' : '#28a745'
                          }}
                        >
                          <FontAwesomeIcon icon={service.is_active ? faEyeSlash : faEye} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleReorderService(service.id, "up")}
                          disabled={index === 0}
                          title="Mută în sus"
                          style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}
                        >
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => handleReorderService(service.id, "down")}
                          disabled={index === filteredServices.length - 1}
                          title="Mută în jos"
                          style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}
                        >
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                        <button
                          className="action-btn cancel-btn"
                          onClick={() => handleDeleteService(service.id)}
                          title="Șterge"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Details Modal */}
      {selectedService && (
        <div className="booking-modal-overlay">
          <div className="booking-modal">
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <FontAwesomeIcon icon={faStethoscope} />
                </div>
                <div className="modal-title-section">
                  <h2 className="modal-title">{selectedService.title}</h2>
                  <p className="modal-subtitle">Detalii complete despre serviciul medical</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedService(null)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-content-grid">
                <div className="info-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faInfoCircle} className="section-icon" />
                    Informații generale
                  </h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Nume serviciu:</span>
                      <span className="info-value">{selectedService.title}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Categorie:</span>
                      <span className="info-value">{selectedService.category}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Preț:</span>
                      <span className="info-value">{selectedService.price}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Status:</span>
                      <span className={`info-value ${selectedService.is_active ? 'text-success' : 'text-danger'}`}>
                        {selectedService.is_active ? 'Activ' : 'Inactiv'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="symptoms-section">
                  <h4 className="section-title">
                    <FontAwesomeIcon icon={faEdit} className="section-icon" />
                    Descriere
                  </h4>
                  <div className="symptoms-content">
                    <p>{selectedService.description}</p>
                  </div>
                </div>

                {selectedService.image_url && (
                  <div className="info-section">
                    <h4 className="section-title">
                      <FontAwesomeIcon icon={faImage} className="section-icon" />
                      Imagine
                    </h4>
                    <div className="info-grid">
                      <div className="info-item full-width">
                        <img 
                          src={selectedService.image_url} 
                          alt={selectedService.title}
                          style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-modal-secondary"
                onClick={() => setSelectedService(null)}
              >
                <FontAwesomeIcon icon={faTimes} className="btn-icon" />
                Închide
              </button>
              <button
                className="btn-modal-primary"
                onClick={() => {
                  setSelectedService(null);
                  handleEditService(selectedService);
                }}
              >
                <FontAwesomeIcon icon={faEdit} className="btn-icon" />
                Editează Serviciu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Loading Overlay */}
      {updating && (
        <div className="update-loading-overlay">
          <div className="update-loading-content">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Se procesează...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManager;
